import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useModalTab } from '../search/WebshopConfigSearch';
import {
    Box, Button, Chip, IconButton, MenuItem, Paper,
    Slider, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import AddIcon                 from '@mui/icons-material/Add';
import CloseIcon               from '@mui/icons-material/Close';
import DeleteOutlineIcon       from '@mui/icons-material/DeleteOutline';
import ReplayIcon              from '@mui/icons-material/Replay';
import FormatBoldIcon          from '@mui/icons-material/FormatBold';
import FormatItalicIcon        from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon    from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon      from '@mui/icons-material/StrikethroughS';
import { useWebshopLanguages } from '../WebshopLanguagesContext';
import { LanguageBar } from '../_LanguageBar';

// Grid-specific config modal. Mirrors the Carousel modal's two-tab layout
// (Images | Settings) and language switcher, but the settings are grid-shaped
// — columns/rows/gap instead of slide timing and transitions.

const ALL_LANGS = [
    { code: 'nl', short: 'NL', label: 'Nederlands' },
    { code: 'fr', short: 'FR', label: 'Français'   },
    { code: 'en', short: 'EN', label: 'English'    },
];

const HOVER_EFFECTS = [
    { v: 'none',    l: 'None'     },
    { v: 'zoom',    l: 'Zoom in'  },
    { v: 'darken',  l: 'Darken'   },
    { v: 'fade',    l: 'Fade'     },
    { v: 'slideUp', l: 'Slide up' },
];
// Nine-position grid — matches the legacy admin's dropdown (admin-config
// component.html:797-812) so loaded values map cleanly. Backend stores the
// space-separated form ('middle center', 'bottom right', …); the parent
// screen converts ↔ hyphen on load/save.
const POSITIONS = [
    { v: 'top-left',      l: 'Top left'      },
    { v: 'top-center',    l: 'Top center'    },
    { v: 'top-right',     l: 'Top right'     },
    { v: 'middle-left',   l: 'Middle left'   },
    { v: 'middle-center', l: 'Middle center' },
    { v: 'middle-right',  l: 'Middle right'  },
    { v: 'bottom-left',   l: 'Bottom left'   },
    { v: 'bottom-center', l: 'Bottom center' },
    { v: 'bottom-right',  l: 'Bottom right'  },
];

const blankCell = (i = 1) => ({
    id:               `c_${Date.now()}_${i}`,
    order:            i,
    active:           true,
    // Per-cell grid span. `width` is column-span, `height` is row-span;
    // the storefront passes them to <mat-grid-tile> as [colspan]/[rowspan]
    // (home.component.html:55-57). Default 1×1 so a fresh cell occupies a
    // single tile until the user widens it.
    width:            1,
    height:           1,
    desktopImage:     null,
    alternativeImage: null,
    linkUrl:          '',
    hoverDesktop:     '',
    hoverMobile:      '',
});

// Default state shape — used when the modal mounts uncontrolled, and
// exported so the parent can mirror the same shape when wiring real data.
// The textOverlay shape mirrors the carousel modal exactly (desktop + mobile
// sub-objects + three shared flags) because the legacy storefront backs both
// with the same `overlay.desktop` / `overlay.mobile` JSON structure (mig166
// for image grid; HOME_GALLERY_CONFIG for carousel).
// Settings are PER LANGUAGE ({ nl, en, fr }) — the language pills at the top
// of the modal apply to the Settings tab too.
export const IMAGE_GRID_LANG_SETTINGS_DEFAULT = {
    layout: {
        columns:           3,
        gap:               8,
        // [top, bottom] margin around the grid block, in px. Backed by
        // IMAGE_GRID_CONFIG_{lang}.margin (mig164).
        margin:            [20, 0],
        // "Use the same grid configuration for smaller screens." Backed
        // by IMAGE_GRID_KEEP_GRID_ON_MOBILE_{lang} (mig112 + mig158).
        keepGridOnMobile:  true,
    },
    hover: {
        effect:         'zoom',
        zoomLevel:      120,
        animationSpeed: 0.3,
    },
    textOverlay: {
        desktop: { background: '#ffffff', textColor: '#000000', position: 'bottom-center', size: 12, width: 100, bold: false, italic: false, underline: false, strike: false },
        mobile:  { background: '#ffffff', textColor: '#000000', position: 'bottom-center', size: 12, width: 10,  bold: false, italic: false, underline: false, strike: false },
        onlyOnHover:        true,
        alwaysVisible:      false,
        useDesktopOnMobile: true,
    },
};
export const DEFAULT_IMAGE_GRID_CONFIG = {
    cells: { nl: [], en: [], fr: [] },
    settings: {
        nl: IMAGE_GRID_LANG_SETTINGS_DEFAULT,
        en: IMAGE_GRID_LANG_SETTINGS_DEFAULT,
        fr: IMAGE_GRID_LANG_SETTINGS_DEFAULT,
    },
};

const ImageGridConfigModal = ({ value, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    const [tab, setTab]         = useState(0); // 0=Images, 1=Settings
    const [langTab, setLangTab] = useState(0);
    // Let the global search jump straight to the Images/Settings tab.
    useModalTab('imageGrid', setTab);

    // Dual mode: when both value+onChange come from the parent we're
    // controlled and never touch local state; otherwise we own a copy.
    const controlled = value !== undefined && typeof onChange === 'function';
    const [localConfig, setLocalConfig] = useState(DEFAULT_IMAGE_GRID_CONFIG);
    const config = controlled ? value : localConfig;
    const setConfig = (updater) => {
        if (controlled) {
            const next = typeof updater === 'function' ? updater(config) : updater;
            onChange(next);
        } else {
            setLocalConfig(updater);
        }
    };

    // Only show language tabs the admin is allowed to edit (ADMIN_LANGUAGES).
    const { adminLanguages } = useWebshopLanguages();
    const LANGS = ALL_LANGS.filter(l => adminLanguages.includes(String(l.code).toUpperCase()));
    const activeLangIdx = (langTab >= 0 && langTab < LANGS.length) ? langTab : 0;
    const lang = (LANGS[activeLangIdx] || ALL_LANGS[0]).code;
    const cellsForLang = config.cells[lang] || [];
    const setCells = (newList) => setConfig(p => ({ ...p, cells: { ...p.cells, [lang]: newList } }));
    const updateCell = (id, patch) => setCells(cellsForLang.map(c => c.id === id ? { ...c, ...patch } : c));
    // Existing (server-backed) cells stay visible as "pending delete" until
    // Save commits the DELETE — the user can undo. Brand-new cells (no
    // serverId) just disappear; there is nothing to restore.
    const removeCell = (id) => {
        const cell = cellsForLang.find(c => c.id === id);
        if (cell && cell.serverId != null) {
            updateCell(id, { pendingDelete: true });
        } else {
            setCells(cellsForLang.filter(c => c.id !== id));
        }
    };
    const restoreCell = (id) => updateCell(id, { pendingDelete: false });
    const addCell    = () => setCells([...cellsForLang, blankCell(cellsForLang.length + 1)]);
    const copyFrom   = (fromLang) => {
        if (fromLang === lang) return;
        if (tab === 1) {
            // Settings tab: copy the source language's settings onto the
            // active one, but KEEP the target's raw server JSON (the
            // per-language merge base for unknown legacy fields).
            setConfig(p => {
                const src  = p.settings?.[fromLang] || {};
                const dest = p.settings?.[lang]     || {};
                return {
                    ...p,
                    settings: {
                        ...p.settings,
                        [lang]: { ...src, __rawGridConfig: dest.__rawGridConfig },
                    },
                };
            });
            return;
        }
        const src = (config.cells[fromLang] || []).map(c => ({ ...c, id: `c_${Date.now()}_${Math.random()}` }));
        setCells(src);
    };

    // ────────── Settings handlers (per language) ──────────
    const langSettings = config.settings?.[lang] || IMAGE_GRID_LANG_SETTINGS_DEFAULT;
    const setLangSettings = (updater) => setConfig(p => {
        const cur = p.settings?.[lang] || IMAGE_GRID_LANG_SETTINGS_DEFAULT;
        return { ...p, settings: { ...p.settings, [lang]: updater(cur) } };
    });
    const setSettings = (section) => (patch) => setLangSettings(ls => ({
        ...ls,
        [section]: { ...(ls[section] || {}), ...patch },
    }));
    // Per-device overlay setter — mirrors CarouselConfigModal so the two
    // modals share UX (the user expects the dual desktop+mobile cards from
    // the legacy admin's "Overlay voorkeuren" panels).
    const setOverlay = (device) => (patch) => setLangSettings(ls => ({
        ...ls,
        textOverlay: {
            ...ls.textOverlay,
            [device]: { ...((ls.textOverlay || {})[device] || {}), ...patch },
        },
    }));
    const setOverlayFlag = (key, val) => setLangSettings(ls => ({
        ...ls,
        textOverlay: { ...ls.textOverlay, [key]: val },
    }));

    const activeCount = cellsForLang.filter(c => c.active).length;

    return (
        <Box>
            {/* Language switcher + COPY FROM */}
            <LanguageBar
                langs={LANGS}
                activeCode={lang}
                onSelect={(code) => setLangTab(LANGS.findIndex(l => l.code === code))}
                onCopyFrom={copyFrom}
                tr={tr}
            />

            <Tabs
                value={tab}
                onChange={(_e, v) => setTab(v)}
                centered
                sx={{
                    minHeight: 40, borderBottom: '1px solid', borderColor: 'divider', mb: 2,
                    '& .MuiTab-root': { minHeight: 40, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5, fontSize: '0.85rem' },
                }}
            >
                <Tab label={tr('cfg_imagegrid_tab_images',   'Images')} />
                <Tab label={tr('cfg_imagegrid_tab_settings', 'Settings')} />
            </Tabs>

            {tab === 0 && (
                <ImagesPanel
                    cells={cellsForLang}
                    activeCount={activeCount}
                    onUpdate={updateCell}
                    onRemove={removeCell}
                    onRestore={restoreCell}
                    onAdd={addCell}
                    tr={tr}
                />
            )}
            {tab === 1 && (
                <SettingsPanel
                    settings={langSettings}
                    setSection={setSettings}
                    setOverlay={setOverlay}
                    setOverlayFlag={setOverlayFlag}
                    cellCount={cellsForLang.length}
                    tr={tr}
                    theme={theme}
                />
            )}
        </Box>
    );
};
ImageGridConfigModal.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

// ──────────────────────────────────────────────────────────────────
// Images panel — cell cards + add button
// ──────────────────────────────────────────────────────────────────
const ImagesPanel = ({ cells, activeCount, onUpdate, onRemove, onRestore, onAdd, tr }) => (
    <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {tr('cfg_imagegrid_active_count', '{{active}} of {{total}} cells active')
                    .replace('{{active}}', activeCount).replace('{{total}}', cells.length)}
            </Typography>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onAdd} sx={{ textTransform: 'none', borderRadius: 1.5 }}>
                {tr('cfg_imagegrid_add_cell', 'Add cell')}
            </Button>
        </Box>
        <Stack spacing={1.5}>
            {cells.map((c, idx) => (
                <CellCard key={c.id} cell={c} index={idx + 1} onUpdate={onUpdate} onRemove={onRemove} onRestore={onRestore} tr={tr} />
            ))}
            {cells.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
                    <Typography variant="body2">{tr('cfg_imagegrid_empty', 'No cells yet — add one above to start.')}</Typography>
                </Box>
            )}
        </Stack>
    </Box>
);
ImagesPanel.propTypes = { cells: PropTypes.array, activeCount: PropTypes.number, onUpdate: PropTypes.func, onRemove: PropTypes.func, onRestore: PropTypes.func, onAdd: PropTypes.func, tr: PropTypes.func };

// ──────────────────────────────────────────────────────────────────
// Single cell card — header (toggle, index, delete), images, link, hover text
// ──────────────────────────────────────────────────────────────────
const CellCard = ({ cell, index, onUpdate, onRemove, onRestore, tr }) => (
    // Subtle grey card backdrop (matches the carousel slide cards) so the
    // white inputs/image slots inside pop out. Pending-delete cells stay
    // visible (greyed, locked) until Save commits the DELETE; undo restores.
    <Paper variant="outlined" sx={{
        borderRadius: 2, p: 2.5,
        backgroundColor: cell.pendingDelete ? 'rgba(211,47,47,0.04)' : 'rgba(0,0,0,0.02)',
        ...(cell.pendingDelete ? { borderColor: 'error.light', borderStyle: 'dashed' } : {}),
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{
                width: 28, height: 28, borderRadius: '50%',
                backgroundColor: cell.active ? 'primary.main' : 'rgba(0,0,0,0.1)',
                color: cell.active ? '#fff' : 'text.disabled',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 700,
                opacity: cell.pendingDelete ? 0.45 : 1,
            }}>{index}</Box>
            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600, opacity: cell.pendingDelete ? 0.45 : 1 }}>
                {tr('cfg_imagegrid_cell', 'Image')} {index}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: cell.pendingDelete ? 0.45 : 1, pointerEvents: cell.pendingDelete ? 'none' : 'auto' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {cell.active ? tr('cfg_imagegrid_visible', 'Visible') : tr('cfg_imagegrid_hidden', 'Hidden')}
                </Typography>
                <Switch size="small" color="success" checked={!!cell.active}
                    onChange={(e) => onUpdate(cell.id, { active: e.target.checked })} />
            </Box>
            {cell.pendingDelete ? (
                <>
                    <Chip size="small" color="error" variant="outlined"
                        label={tr('cfg_imagegrid_pending_delete', 'Will be deleted on save')} />
                    <Button
                        size="small" variant="outlined"
                        startIcon={<ReplayIcon fontSize="small" />}
                        onClick={() => onRestore(cell.id)}
                        sx={{ textTransform: 'none', borderRadius: 1.5, flexShrink: 0 }}
                    >
                        {tr('cfg_imagegrid_undo_delete', 'Undo')}
                    </Button>
                </>
            ) : (
                /* Clear, labelled delete button (matches the carousel modal's
                    "Delete slide") instead of a bare trash icon. */
                <Button
                    size="small" color="error" variant="outlined"
                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                    onClick={() => onRemove(cell.id)}
                    sx={{ textTransform: 'none', borderRadius: 1.5, flexShrink: 0 }}
                >
                    {tr('cfg_imagegrid_remove_cell', 'Remove image')}
                </Button>
            )}
        </Box>

        <Box sx={{ opacity: cell.pendingDelete ? 0.45 : 1, pointerEvents: cell.pendingDelete ? 'none' : 'auto' }}>
        {/* Per-cell grid span — storefront uses these as [colspan]/[rowspan]
            on <mat-grid-tile>. Defaults to 1×1; widen for hero tiles. */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <Box>
                <UpperLabel>{tr('cfg_imagegrid_cell_cols', 'COLUMNS')}</UpperLabel>
                <TextField
                    size="small" type="number"
                    value={cell.width ?? 1}
                    onChange={(e) => onUpdate(cell.id, { width: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                    inputProps={{ min: 1, step: 1 }}
                    sx={{ width: 110, backgroundColor: '#fff' }}
                />
            </Box>
            <Box>
                <UpperLabel>{tr('cfg_imagegrid_cell_rows', 'ROWS')}</UpperLabel>
                <TextField
                    size="small" type="number"
                    value={cell.height ?? 1}
                    onChange={(e) => onUpdate(cell.id, { height: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                    inputProps={{ min: 1, step: 1 }}
                    sx={{ width: 110, backgroundColor: '#fff' }}
                />
            </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <ImageSlot
                label={tr('cfg_imagegrid_desktop_img', 'DESKTOP IMAGE')}
                value={cell.desktopImage}
                onChange={(v) => onUpdate(cell.id, { desktopImage: v })}
                tr={tr}
            />
            <ImageSlot
                label={tr('cfg_imagegrid_alt_img', 'ALTERNATIVE IMAGE')}
                value={cell.alternativeImage}
                onChange={(v) => onUpdate(cell.id, { alternativeImage: v })}
                tr={tr}
            />
        </Box>

        <Box sx={{ mb: 2 }}>
            <UpperLabel>{tr('cfg_imagegrid_link', 'LINK URL')}</UpperLabel>
            <TextField
                fullWidth size="small"
                placeholder="https://..."
                value={cell.linkUrl || ''}
                onChange={(e) => onUpdate(cell.id, { linkUrl: e.target.value })}
                sx={{ backgroundColor: '#fff' }}
            />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
                <UpperLabel>{tr('cfg_imagegrid_caption_desktop', 'CAPTION (DESKTOP)')}</UpperLabel>
                <TextField
                    fullWidth size="small"
                    value={cell.hoverDesktop || ''}
                    onChange={(e) => onUpdate(cell.id, { hoverDesktop: e.target.value })}
                    sx={{ backgroundColor: '#fff' }}
                />
            </Box>
            <Box>
                <UpperLabel>{tr('cfg_imagegrid_caption_mobile', 'CAPTION (MOBILE)')}</UpperLabel>
                <TextField
                    fullWidth size="small"
                    value={cell.hoverMobile || ''}
                    onChange={(e) => onUpdate(cell.id, { hoverMobile: e.target.value })}
                    sx={{ backgroundColor: '#fff' }}
                />
            </Box>
        </Box>
        </Box>
    </Paper>
);
CellCard.propTypes = { cell: PropTypes.object, index: PropTypes.number, onUpdate: PropTypes.func, onRemove: PropTypes.func, onRestore: PropTypes.func, tr: PropTypes.func };

// ──────────────────────────────────────────────────────────────────
// Image/video upload slot — empty placeholder OR preview thumbnail.
// Mime-aware: image/* renders as <img>, video/* renders as <video>.
// Accepts either `value.url` (server file or blob) or `value.dataUrl`
// (base64 from a FileReader); displays whichever is present.
// ──────────────────────────────────────────────────────────────────
const ImageSlot = ({ label, value, onChange, tr }) => {
    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const url = URL.createObjectURL(f);
        // Keep the raw File so the save flow can upload it (base64 via the JSON
        // proxy). Existing/server images have no `file`.
        onChange({ name: f.name, url, mime: f.type || '', file: f });
    };
    const clear = () => onChange(null);
    const src     = value?.url || value?.dataUrl || '';
    const isVideo = !!(value?.mime && value.mime.startsWith('video/'));
    return (
        <Box>
            <UpperLabel>{label}</UpperLabel>
            <Box sx={{
                position: 'relative',
                border: '1px dashed', borderColor: 'divider', borderRadius: 1.5,
                // White slot so it pops against the grey card backdrop (matches carousel).
                backgroundColor: '#fff',
                minHeight: 130,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
            }}>
                {src ? (
                    <>
                        {isVideo ? (
                            <Box component="video" src={src} controls preload="metadata"
                                sx={{ maxWidth: '100%', maxHeight: 180, display: 'block', backgroundColor: '#000' }} />
                        ) : (
                            <Box component="img" src={src} alt={value.name || 'preview'}
                                sx={{ maxWidth: '100%', maxHeight: 180, display: 'block' }} />
                        )}
                        <IconButton
                            size="small" onClick={clear} title={tr('cfg_imagegrid_clear_image', 'Remove image')}
                            sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(255,255,255,0.85)' }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </>
                ) : (
                    <Button component="label" size="small" variant="outlined" startIcon={<AddIcon />} sx={{ textTransform: 'none', borderRadius: 1.5 }}>
                        {tr('cfg_imagegrid_upload', 'Upload image')}
                        <input type="file" accept="image/*,video/*" onChange={handleFile} style={{ display: 'none' }} />
                    </Button>
                )}
            </Box>
        </Box>
    );
};
ImageSlot.propTypes = { label: PropTypes.node, value: PropTypes.object, onChange: PropTypes.func, tr: PropTypes.func };

// ──────────────────────────────────────────────────────────────────
// Settings panel — layout / display / hover / text overlay
// ──────────────────────────────────────────────────────────────────
const SettingsPanel = ({ settings, setSection, setOverlay, setOverlayFlag, cellCount, tr }) => {
    const setLayout = setSection('layout');
    const setHover  = setSection('hover');

    const margin = Array.isArray(settings.layout.margin) ? settings.layout.margin : [20, 0];

    return (
        <Stack spacing={2}>
            <Card title={tr('cfg_imagegrid_layout_title', 'Layout')} desc={tr('cfg_imagegrid_layout_desc', 'How the cells are arranged on the page.')}>
                <SettingRow
                    label={tr('cfg_imagegrid_columns', 'Columns')}
                    help={tr('cfg_imagegrid_columns_help', 'Number of cells side-by-side on desktop. Smaller screens use fewer columns automatically.')}
                    control={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 260 }}>
                            <Slider size="small" min={1} max={6} step={1}
                                value={settings.layout.columns}
                                onChange={(_e, v) => setLayout({ columns: v })}
                                marks valueLabelDisplay="auto" sx={{ flex: 1 }} />
                            <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 16, textAlign: 'right' }}>
                                {settings.layout.columns}
                            </Typography>
                        </Box>
                    }
                />
                <SettingRow
                    label={tr('cfg_imagegrid_gap', 'Gap between cells')}
                    help={tr('cfg_imagegrid_gap_help', 'Spacing between cells. 0 makes them touch.')}
                    control={
                        <TextField type="number" size="small"
                            value={settings.layout.gap}
                            onChange={(e) => setLayout({ gap: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                            InputProps={{ endAdornment: <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>px</Typography> }}
                            sx={{ width: 120 }}
                        />
                    }
                />
                <SettingRow
                    label={tr('cfg_imagegrid_keep_on_mobile', 'Use the same grid configuration for smaller screens')}
                    help={tr('cfg_imagegrid_keep_on_mobile_help', 'When on, mobile uses the same column count and gap. When off, the grid stacks vertically on small screens.')}
                    control={<Switch size="small" color="success"
                        checked={!!settings.layout.keepGridOnMobile}
                        onChange={(e) => setLayout({ keepGridOnMobile: e.target.checked })} />}
                />
                <SettingRow
                    label={tr('cfg_imagegrid_margin', 'Extra margin around grid')}
                    help={tr('cfg_imagegrid_margin_help', 'Whitespace above and below the grid block.')}
                    align="top"
                    control={
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                            <TextField type="number" size="small"
                                label={tr('cfg_imagegrid_margin_top', 'Top')}
                                value={margin[0] ?? 0}
                                onChange={(e) => setLayout({ margin: [Math.max(0, parseInt(e.target.value, 10) || 0), margin[1] ?? 0] })}
                                InputProps={{ endAdornment: <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>px</Typography> }}
                                sx={{ width: 110 }}
                            />
                            <TextField type="number" size="small"
                                label={tr('cfg_imagegrid_margin_bottom', 'Bottom')}
                                value={margin[1] ?? 0}
                                onChange={(e) => setLayout({ margin: [margin[0] ?? 0, Math.max(0, parseInt(e.target.value, 10) || 0)] })}
                                InputProps={{ endAdornment: <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>px</Typography> }}
                                sx={{ width: 110 }}
                            />
                        </Box>
                    }
                />
            </Card>

            <Card title={tr('cfg_imagegrid_hover_title', 'Hover effect')} desc={tr('cfg_imagegrid_hover_desc', 'What happens when a visitor hovers a cell on desktop.')}>
                <SettingRow
                    label={tr('cfg_imagegrid_hover_effect', 'Effect')}
                    control={
                        <TextField select size="small" value={settings.hover.effect}
                            onChange={(e) => setHover({ effect: e.target.value })}
                            sx={{ minWidth: 180 }}>
                            {HOVER_EFFECTS.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                        </TextField>
                    }
                />
                {settings.hover.effect === 'zoom' && (
                    <SettingRow
                        label={tr('cfg_imagegrid_hover_zoom', 'Zoom level')}
                        help={tr('cfg_imagegrid_hover_zoom_help', 'How much the image scales up on hover.')}
                        control={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 260 }}>
                                <Slider size="small" min={100} max={200} step={5}
                                    value={settings.hover.zoomLevel}
                                    onChange={(_e, v) => setHover({ zoomLevel: v })}
                                    valueLabelDisplay="auto" sx={{ flex: 1 }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 36, textAlign: 'right' }}>
                                    {settings.hover.zoomLevel}%
                                </Typography>
                            </Box>
                        }
                    />
                )}
                <SettingRow
                    label={tr('cfg_imagegrid_hover_speed', 'Animation speed')}
                    help={tr('cfg_imagegrid_hover_speed_help', 'How fast the hover transition plays.')}
                    control={
                        <TextField type="number" size="small"
                            inputProps={{ step: 0.05, min: 0.05, max: 2 }}
                            value={settings.hover.animationSpeed}
                            onChange={(e) => setHover({ animationSpeed: parseFloat(e.target.value) || 0.3 })}
                            InputProps={{ endAdornment: <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>s</Typography> }}
                            sx={{ width: 120 }}
                        />
                    }
                />
            </Card>

            <Card title={tr('cfg_imagegrid_overlay_title', 'Caption overlay')} desc={tr('cfg_imagegrid_overlay_desc', 'Styling for the optional text shown on each cell. The caption per cell comes from the Images tab.')}>
                {/* Two side-by-side device editors, mirroring the legacy
                    admin's "Overlay voorkeuren voor Rooster-item (desktop)"
                    and "(mobiel)" panels — same shape the carousel modal
                    uses. The three shared flags live below, scoped to
                    desktop where the legacy admin labels them as such. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <OverlayDeviceCard label={tr('cfg_imagegrid_overlay_desktop', 'Desktop')} data={settings.textOverlay.desktop} onChange={setOverlay('desktop')} tr={tr} />
                    <OverlayDeviceCard label={tr('cfg_imagegrid_overlay_mobile',  'Mobile')}  data={settings.textOverlay.mobile}  onChange={setOverlay('mobile')}  tr={tr} />
                </Box>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1.5, pt: 1.5 }}>
                    <SettingRow
                        label={tr('cfg_imagegrid_overlay_only_hover', 'Only show on hover (desktop)')}
                        help={tr('cfg_imagegrid_overlay_only_hover_help', 'Captions stay hidden until the visitor hovers a cell.')}
                        control={<Switch size="small" color="success"
                            checked={!!settings.textOverlay.onlyOnHover}
                            onChange={(e) => setOverlayFlag('onlyOnHover', e.target.checked)} />}
                    />
                    <SettingRow
                        label={tr('cfg_imagegrid_overlay_always', 'Keep text always visible (desktop)')}
                        control={<Switch size="small" color="success"
                            checked={!!settings.textOverlay.alwaysVisible}
                            onChange={(e) => setOverlayFlag('alwaysVisible', e.target.checked)} />}
                    />
                    <SettingRow
                        label={tr('cfg_imagegrid_overlay_use_desktop_mobile', 'Use desktop caption on mobile if mobile is empty')}
                        control={<Switch size="small" color="success"
                            checked={!!settings.textOverlay.useDesktopOnMobile}
                            onChange={(e) => setOverlayFlag('useDesktopOnMobile', e.target.checked)} />}
                    />
                </Box>
            </Card>

            <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center', display: 'block' }}>
                {tr('cfg_imagegrid_cell_summary', '{{n}} cells configured in this language.').replace('{{n}}', cellCount)}
            </Typography>
        </Stack>
    );
};
SettingsPanel.propTypes = {
    settings: PropTypes.object, setSection: PropTypes.func,
    setOverlay: PropTypes.func, setOverlayFlag: PropTypes.func,
    cellCount: PropTypes.number, tr: PropTypes.func, theme: PropTypes.string,
};

// ──────────────────────────────────────────────────────────────────
// Single-device overlay card (desktop OR mobile) — mirrors the layout
// of the legacy admin's "Overlay voorkeuren voor Rooster-item" panel
// and is structurally identical to CarouselConfigModal's same-named
// helper (kept duplicated rather than extracted because the supporting
// primitives — ColorSwatch / UpperLabel / StyleToggle / POSITIONS —
// are already local to each modal).
// ──────────────────────────────────────────────────────────────────
const OverlayDeviceCard = ({ label, data = {}, onChange, tr }) => (
    <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>{label}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
            <Box>
                <UpperLabel>{tr('cfg_imagegrid_overlay_bg', 'BACKGROUND')}</UpperLabel>
                <ColorSwatch value={data.background} onChange={(v) => onChange({ background: v })} />
            </Box>
            <Box>
                <UpperLabel>{tr('cfg_imagegrid_overlay_text_color', 'TEXT COLOR')}</UpperLabel>
                <ColorSwatch value={data.textColor} onChange={(v) => onChange({ textColor: v })} />
            </Box>
        </Box>
        <Box sx={{ mb: 1.5 }}>
            <UpperLabel>{tr('cfg_imagegrid_overlay_position', 'POSITION')}</UpperLabel>
            <TextField select size="small" fullWidth
                value={data.position || 'bottom-center'}
                onChange={(e) => onChange({ position: e.target.value })}
                sx={{ backgroundColor: '#fff' }}
            >
                {POSITIONS.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
            </TextField>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
            <Box>
                <UpperLabel>{tr('cfg_imagegrid_overlay_size', 'SIZE')}</UpperLabel>
                <TextField size="small" type="number" fullWidth
                    value={data.size ?? 12}
                    onChange={(e) => onChange({ size: Math.max(8, parseInt(e.target.value) || 0) })}
                    InputProps={{ endAdornment: <Typography variant="caption" sx={{ color: 'text.secondary' }}>px</Typography> }}
                    sx={{ backgroundColor: '#fff' }}
                />
            </Box>
            <Box>
                <UpperLabel>{tr('cfg_imagegrid_overlay_width', 'WIDTH')}</UpperLabel>
                <TextField size="small" type="number" fullWidth
                    value={data.width ?? 10}
                    onChange={(e) => onChange({ width: Math.max(0, parseInt(e.target.value) || 0) })}
                    InputProps={{ endAdornment: <Typography variant="caption" sx={{ color: 'text.secondary' }}>%</Typography> }}
                    sx={{ backgroundColor: '#fff' }}
                />
            </Box>
        </Box>
        <Box>
            <UpperLabel>{tr('cfg_imagegrid_overlay_style', 'STYLE')}</UpperLabel>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <StyleToggle active={!!data.bold}      onClick={() => onChange({ bold:      !data.bold })}>     <FormatBoldIcon       fontSize="small" /></StyleToggle>
                <StyleToggle active={!!data.italic}    onClick={() => onChange({ italic:    !data.italic })}>   <FormatItalicIcon     fontSize="small" /></StyleToggle>
                <StyleToggle active={!!data.underline} onClick={() => onChange({ underline: !data.underline })}><FormatUnderlinedIcon fontSize="small" /></StyleToggle>
                <StyleToggle active={!!data.strike}    onClick={() => onChange({ strike:    !data.strike })}>   <StrikethroughSIcon   fontSize="small" /></StyleToggle>
            </Box>
        </Box>
    </Box>
);
OverlayDeviceCard.propTypes = { label: PropTypes.node, data: PropTypes.object, onChange: PropTypes.func, tr: PropTypes.func };

// ──────────────────────────────────────────────────────────────────
// Small helpers — Card / SettingRow / UpperLabel / ColorSwatch / StyleToggle
// ──────────────────────────────────────────────────────────────────
const Card = ({ title, desc, children }) => (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
            {desc && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{desc}</Typography>}
        </Box>
        <Stack spacing={1}>{children}</Stack>
    </Paper>
);
Card.propTypes = { title: PropTypes.node, desc: PropTypes.node, children: PropTypes.node };

const SettingRow = ({ label, help, control }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>{help}</Typography>}
        </Box>
        <Box sx={{ flexShrink: 0 }}>{control}</Box>
    </Box>
);
SettingRow.propTypes = { label: PropTypes.node, help: PropTypes.node, control: PropTypes.node };

const UpperLabel = ({ children }) => (
    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem', mb: 0.5, textTransform: 'uppercase' }}>
        {children}
    </Typography>
);
UpperLabel.propTypes = { children: PropTypes.node };

// Kept visually identical to CarouselConfigModal's ColorSwatch so the two
// overlay editors look the same — a hidden native <input type="color">
// behind a 36px swatch tile that previews the selected colour. No inline
// hex readout (the colour is conveyed by the swatch itself).
const ColorSwatch = ({ value, onChange }) => (
    <Box component="label" sx={{
        position: 'relative', display: 'block',
        width: 36, height: 36, borderRadius: 1,
        border: '1px solid rgba(0,0,0,0.18)',
        backgroundColor: value || '#ffffff',
        cursor: 'pointer',
    }}>
        <input type="color" value={value || '#ffffff'} onChange={(e) => onChange(e.target.value)}
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
    </Box>
);
ColorSwatch.propTypes = { value: PropTypes.string, onChange: PropTypes.func };

// Same visual treatment as the carousel modal's StyleToggle — MUI
// IconButton with a thin outline that fills in when active.
const StyleToggle = ({ active, onClick, children }) => (
    <IconButton
        size="small"
        onClick={onClick}
        sx={{
            border: '1px solid',
            borderColor: active ? 'primary.main' : 'divider',
            borderRadius: 1,
            color: active ? 'primary.main' : 'text.primary',
            backgroundColor: active ? 'rgba(25,118,210,0.08)' : 'transparent',
            width: 34, height: 34,
        }}
    >
        {children}
    </IconButton>
);
StyleToggle.propTypes = { active: PropTypes.bool, onClick: PropTypes.func, children: PropTypes.node };

export default ImageGridConfigModal;
